<template>
  <div class="character-form-component">
    <!-- 头像 -->
    <div class="avatar-setting-section">
      <div class="avatar-display-wrapper" @click="$emit('avatar-click')">
        <div class="avatar-circle">
          <img
            v-if="localForm.avatar"
            :src="localForm.avatar"
            alt="虚拟对象头像"
            class="avatar-img"
            @error="handleAvatarError"
          >
          <svg v-else width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="8" r="4" stroke="#FF7E7E" stroke-width="1.5"/>
            <path d="M4 20c0-4 3.58-7 8-7s8 3 8 7" stroke="#FF7E7E" stroke-width="1.5"/>
          </svg>
        </div>
        <div class="avatar-hint-text">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
          点击更换头像
        </div>
      </div>
    </div>

    <!-- 表单 -->
    <el-form
      ref="formRef"
      :model="localForm"
      :rules="rules"
      label-width="75px"
      class="cf-form"
    >
      <el-form-item label="名字" prop="name">
        <el-input v-model="localForm.name" placeholder="给TA起个名字" clearable size="large" />
      </el-form-item>

      <div class="form-row">
        <el-form-item label="性别" prop="gender" class="gender-item">
          <el-radio-group v-model="localForm.gender" size="large">
            <el-radio :label="1">男</el-radio>
            <el-radio :label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="年龄" class="age-item">
          <el-input-number v-model="localForm.age" :min="1" :max="100" size="large" />
        </el-form-item>
      </div>

      <el-form-item label="职业">
        <el-input v-model="localForm.job" placeholder="TA的职业是什么 (如: 设计师、程序员)" clearable size="large" />
      </el-form-item>

      <el-form-item label="性格" prop="personality">
        <el-input v-model="localForm.personality" type="textarea" :rows="2" placeholder="描述一下TA的性格 (如: 温柔体贴、活泼开朗)" size="large" />
      </el-form-item>

      <el-form-item label="爱好">
        <el-input v-model="localForm.hobbies" type="textarea" :rows="2" placeholder="TA喜欢做什么 (如: 看电影、旅行)" size="large" />
      </el-form-item>

      <el-form-item label="简介">
        <el-input v-model="localForm.introduction" type="textarea" :rows="3" placeholder="一段简短的自我介绍" size="large" />
      </el-form-item>

      <el-form-item class="form-actions">
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading" size="large" class="submit-btn">
          <span v-if="characterId">保存修改</span>
          <span v-else>立即创建</span>
        </el-button>
        <el-button @click="$emit('reset')" size="large" class="reset-btn">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 快速生成 -->
    <div class="quick-gen-section">
      <el-divider></el-divider>
      <div class="quick-gen-header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        <span>快速生成示例人设</span>
      </div>
      <div class="gen-buttons">
        <button type="button" class="gen-btn" @click="generateSweetGirl">🍬 甜妹系</button>
        <button type="button" class="gen-btn" @click="generateGentleGirl">🌸 温柔系</button>
        <button type="button" class="gen-btn" @click="generateVitalityGirl">☀️ 元气系</button>
        <button type="button" class="gen-btn" @click="generateCoolGirl">🌙 清冷系</button>
        <button type="button" class="gen-btn" @click="generateQueenGirl">👑 御姐系</button>
        <button type="button" class="gen-btn" @click="generateMaleExample">☀️ 阳光男生</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineEmits, defineExpose } from 'vue'
import { ElMessage, ElDivider } from 'element-plus'

const props = defineProps({
  formData: { type: Object, required: true },
  submitLoading: { type: Boolean, default: false },
  characterId: { type: Number, default: null }
})

const emit = defineEmits(['submit', 'reset', 'avatar-click'])

const formRef = ref(null)
const localForm = reactive({ ...props.formData })

watch(() => ({ ...props.formData }), (val) => {
  Object.assign(localForm, val)
}, { deep: true })

watch(localForm, (val) => {
  Object.assign(props.formData, val)
}, { deep: true })

const rules = {
  name: [
    { required: true, message: '请给TA起个名字', trigger: 'blur' },
    { min: 1, max: 20, message: '长度在 1 到 20 个字符', trigger: 'blur' }
  ],
  gender: [{ required: true, message: '请选择性别', trigger: 'change' }],
  personality: [{ required: true, message: '请描述一下TA的性格', trigger: 'blur' }]
}

const validate = (callback) => {
  formRef.value?.validate(callback)
}

const resetFields = () => {
  formRef.value?.resetFields()
}

defineExpose({ validate, resetFields })

const handleSubmit = () => {
  formRef.value?.validate((valid) => {
    if (valid) emit('submit')
  })
}

const handleAvatarError = (e) => { e.target.src = '' }

// --- 快速生成模板 ---
const applyTemplate = (data) => {
  Object.assign(localForm, data)
  ElMessage.success('已生成示例人设')
}

const generateSweetGirl = () => applyTemplate({
  name: '小糖', gender: 2, age: 21, job: '大学生',
  personality: '软萌可爱，爱撒娇，有点小粘人，笑点很低',
  hobbies: '吃甜品，追剧，撸猫，拍美照，玩换装游戏',
  introduction: '哈喽～我是小糖！最喜欢吃草莓蛋糕啦，你愿意陪我一起去探店吗？我会一直粘着你的哦～'
})

const generateGentleGirl = () => applyTemplate({
  name: '晚晚', gender: 2, age: 24, job: '图书馆管理员',
  personality: '温柔体贴，善解人意，安静内敛，很会照顾人',
  hobbies: '看书，养花，泡茶，听古典音乐，写日记',
  introduction: '你好，我是晚晚。我喜欢安静的时光，如果你累了，我可以陪你聊聊天，听你说所有的心事。'
})

const generateVitalityGirl = () => applyTemplate({
  name: '乐乐', gender: 2, age: 22, job: '健身教练',
  personality: '阳光开朗，精力充沛，乐观向上，喜欢运动',
  hobbies: '跑步，瑜伽，爬山，露营，听摇滚音乐',
  introduction: '嗨！我是乐乐！生命在于运动！要不要和我一起去晨跑？我保证能让你每天都元气满满！'
})

const generateCoolGirl = () => applyTemplate({
  name: '清月', gender: 2, age: 25, job: '插画师',
  personality: '清冷独立，话不多但很有想法，内心细腻',
  hobbies: '画画，看展，听纯音乐，一个人旅行，养猫',
  introduction: '我是清月。我不太会说话，但我会用画笔记录下我们的每一个瞬间。'
})

const generateQueenGirl = () => applyTemplate({
  name: '曼妮', gender: 2, age: 27, job: '公司高管',
  personality: '成熟知性，气场强大，做事果断，很有主见',
  hobbies: '品酒，高尔夫，看财经新闻，旅行，健身',
  introduction: '我是曼妮。我欣赏有能力的人，希望我们能成为彼此的依靠。'
})

const generateMaleExample = () => applyTemplate({
  name: '阿泽', gender: 1, age: 26, job: '程序员',
  personality: '阳光开朗，幽默风趣，有点直男但很贴心',
  hobbies: '打篮球，玩游戏，看球赛，健身，旅行',
  introduction: '我是阿泽，一个热爱生活的程序员。虽然有时候有点直男，但我会用我的方式对你好。'
})
</script>

<style scoped>
.character-form-component {
  max-width: 540px;
}

.avatar-setting-section {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border-light);
}

.avatar-display-wrapper {
  text-align: center;
  cursor: pointer;
}

.avatar-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--app-color-primary-soft) 0%, var(--app-bg-soft) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  border: 3px solid var(--app-bg-elevated);
  box-shadow: 0 4px 12px rgba(255, 126, 126, 0.15);
  transition: all 0.2s;
  overflow: hidden;
}

.avatar-display-wrapper:hover .avatar-circle {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.25);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-hint-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: var(--app-color-primary);
  font-weight: 500;
}

.cf-form {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 20px;
}

.gender-item { flex: 1; }
.age-item { flex-shrink: 0; }

.cf-form :deep(.el-input__wrapper),
.cf-form :deep(.el-textarea__inner) {
  border-radius: var(--app-radius-md);
}

.cf-form :deep(.el-form-item__label) {
  font-weight: 500;
  color: var(--app-text-secondary);
}

.form-actions { margin-top: 8px; }

.submit-btn {
  padding: 12px 32px;
  border-radius: var(--app-radius-full);
  font-weight: 500;
}

.reset-btn {
  padding: 12px 28px;
  border-radius: var(--app-radius-full);
}

/* 快速生成 */
.quick-gen-section {
  margin-top: 8px;
}

.quick-gen-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  color: var(--app-text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
}

.gen-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gen-btn {
  padding: 8px 16px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-full);
  background: var(--app-bg-elevated);
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  transition: all 0.2s;
  font-family: inherit;
  color: var(--app-text-secondary);
  flex: 1;
  min-width: calc(33% - 8px);
  text-align: center;
}

.gen-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  background: rgba(108, 92, 231, 0.05);
}

@media (max-width: 480px) {
  .form-row { flex-direction: column; gap: 0; }
  .gen-btn { min-width: calc(50% - 8px); }
}
</style>
